import React from "react";
import config from "../../../config";
import { KeyValue } from "../../ExtraDetails";
import { ApiHandlerPureComponent } from "./ApiHandlerPureComponent";
import { WorkOrder, WorkOrderWithAssignment } from "../interfaces/WorkOrder";
import { handleResponseArray } from "./APIHandlerHelper";

interface Props {
  onAuthError?: () => void;
}

export default class WorkOrderApis extends React.PureComponent<Props> {
  private apiResponseHandler = new ApiHandlerPureComponent({
    onAuthError: this.props.onAuthError,
  });

  private static readonly BASE_URL: string = `${config.apiGateway.WORK_API}/api/WorkOrder/`;

  getAllWorkOrders = async (): Promise<WorkOrder[]> => {
    const data = await this.apiResponseHandler.get(WorkOrderApis.BASE_URL);
    if (!data) {
      return [];
    }
    if (!Array.isArray(data)) {
      return [];
    }
    return data;
  };

  getWorkOrdersWithAssignments = (): Promise<WorkOrderWithAssignment[]> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}WithAssignments/true`;
    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getWorkOrdersByCustomer = (customerId: number): Promise<WorkOrder[]> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}ByCustomerId/${customerId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return data;
    });
  };

  getWorkOrderById = (workorderId: number | string): Promise<WorkOrder> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}${workorderId}`;
    return this.apiResponseHandler.get(apiAddress);
  };

  createWorkorder = async (workOrder: Partial<WorkOrder>): Promise<number> => {
    const apiAddress = WorkOrderApis.BASE_URL;
    const body = {
      ...workOrder,
      extraDetail: this.extraDetailsToAPIFormat(workOrder.extraDetail),
    };

    return this.apiResponseHandler.post(apiAddress, JSON.stringify(body), false);
  };

  updateWorkorder = (workOrder: Partial<WorkOrder>): Promise<any> => {
    var apiAddress = WorkOrderApis.BASE_URL;

    return this.apiResponseHandler.put(apiAddress, JSON.stringify(workOrder), false);
  };

  cancelWorkorder = (workOrderId: number): Promise<any> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}Cancel/${workOrderId}`;
    return this.apiResponseHandler.put(apiAddress);
  };

  getWorkOrderWithAssignment = (workOrderId: number | string): Promise<WorkOrderWithAssignment> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}${workOrderId}/WithAssignments/true`;
    return this.apiResponseHandler.get(apiAddress);
  };

  getWorkOrdersByCustomerWithAssignments = (customerId: number): Promise<WorkOrderWithAssignment[]> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}ByCustomerId/${customerId}/WithAssignments/true`;
    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getWorkOrdersByCustomerAndCompanyWithAssignments = (
    customerId: number,
    companyId: number,
  ): Promise<WorkOrderWithAssignment[]> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}ByCustomerAndCompanyId/customerId/${customerId}/companyId/${companyId}/WithAssignments/true`;
    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  getWorkOrdersByCustomerAndSiteWithAssignments = (
    customerId: number,
    siteId: number,
  ): Promise<WorkOrderWithAssignment[]> => {
    const apiAddress = `${WorkOrderApis.BASE_URL}ByCustomerAndSiteId/customerId/${customerId}/siteId/${siteId}/WithAssignments/true`;
    return this.apiResponseHandler.get(apiAddress).then((data) => handleResponseArray(data));
  };

  private extraDetailsToAPIFormat = (extraDetails: KeyValue[] | undefined): Record<string, string> | null => {
    if (extraDetails == null) {
      return null;
    }

    return extraDetails.reduce(
      (acc, curr) => {
        acc[curr.key] = curr.value;
        return acc;
      },
      {} as Record<string, string>,
    );
  };
}
